import { injectable } from "inversify";
import {
  OptionMapper,
  HttpBaseFilter,
  HttpBaseFiltersResponse,
  IFilterParser,
  GetOptions,
} from "../../lineInfo/lineInfoRepository.types";
import {
  FilterKeyValueType,
  IFilter,
  IOption,
} from "@/domain/entities/filter/filter.types";
import { Result, err, ok } from "neverthrow";
import { ParseError } from "@/repositories/shared/errors/parseError";

function parseOptions<TResponse>(
  response: HttpBaseFiltersResponse<TResponse>,
  optionMapper: OptionMapper<TResponse>,
  dummyOptionName?: string,
  defaultOptionCode?: string,
  getOptions?: GetOptions<TResponse>,
  allowDirectInput = false
): IOption[] {
  const httpOptions = getOptions ? getOptions(response) : response.data;

  // dummyOption을 선택했을 때 optionMetadata 값을 초기화하기 위해 dummyOptionMetadata 값 필요
  const optionMetadataKeys = httpOptions?.[0]
    ? Object.keys(optionMapper(httpOptions[0])?.optionMetadata ?? {})
    : [];
  const dummyOptionMetadata: { [key: string]: string } = {};
  optionMetadataKeys.forEach((key) => {
    dummyOptionMetadata[key] = "";
  });

  const options: IOption[] = [];
  if (dummyOptionName) {
    options.push({
      key: "",
      name: dummyOptionName,
      default: !defaultOptionCode,
      optionMetadata: dummyOptionMetadata,
    });
  }

  httpOptions.map((data: HttpBaseFilter<TResponse>) => {
    const { key, name, displayText, value, optionMetadata } =
      optionMapper(data);
    const option: IOption = {
      key,
      name,
      displayText,
      value,
      default: defaultOptionCode?.split(",").includes(key),
      optionMetadata,
    };
    options.push(option);
  });

  if (allowDirectInput) {
    const directOption: IOption = {
      key: "",
      name: "직접입력",
      isDirectInput: true,
      default: defaultOptionCode === "직접입력",
    };
    options.push(directOption);
  }

  return options;
}

@injectable()
export class FilterParser<TResponse> implements IFilterParser {
  constructor() {}

  toDomain(
    response: HttpBaseFiltersResponse<TResponse>,
    pathParam: string,
    key: string,
    type: FilterKeyValueType,
    optionMapper: OptionMapper<TResponse>,
    multiple?: boolean,
    label?: string,
    dummyOptionName?: string,
    defaultOptionCode?: string,
    parentKey?: string,
    essential?: boolean,
    callback?: (selectedOptions?: IOption[] | IOption) => void,
    getOptions?: GetOptions<TResponse>,
    allowDirectInput?: boolean
  ): Result<IFilter, ParseError> {
    try {
      const options = parseOptions(
        response,
        optionMapper,
        dummyOptionName,
        defaultOptionCode,
        getOptions,
        allowDirectInput
      );

      const filter: IFilter = {
        key,
        type,
        label,
        options,
        availableOptions: options,
        multiple,
        parentKey,
        dummyOptionName,
        defaultOptionCode,
        pathParam,
        essential,
        callback,
      };
      return ok(filter);
    } catch (error: any) {
      console.error(`Parse error: ${error.message}`);
      return err(new ParseError(error.message));
    }
  }
}
